import { sound } from "@pixi/sound";
import { getFeatureSymbolAnimPrefix } from "../../components/TransCoins/WinSeqComponent";
import { WinMeterComponent } from "../../components/winmeters/WinMeterComponent";
import { isSuperQuickBtnOnGlobal, RespinIndex } from "../../config/GlobalVariableConfig";
import { REEL_NUMBERS, SYMBOL_IDS } from "../../config/reelConfig";
import { wait } from "../../core/utils";
import { spinData } from "../../server/getServerResposesFun";
import { DisplayComponent } from "../displays/DisplayComponent";
import { BLANK_SYM_ID, reelOverlayObject, SYMBOLS_PER_REEL_FEATURE } from "./ReelInit";
import { ReelSpin } from "./ReelSpin";

const CELL_STAGGER_MS = 500;

export class ReelWin extends ReelSpin {

    constructor() {
        super();
    }

    public updateReelWin(): void {
        this.updateReelSpin();
    }

    public async animateAllWinningSymbols(): Promise<void> {
        this.openWinCollectionMeter();

        const flyTasks: Promise<void>[] = [];
        this.totalWinVal = 0;

        const timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
       let val = 0;

        for (let reelNum = 0; reelNum < REEL_NUMBERS; reelNum++) {
            for (let posNum = 0; posNum < SYMBOLS_PER_REEL_FEATURE; posNum++) {
                if (spinData.respins[RespinIndex.value].coinValues[reelNum][posNum] === 0) {
                    val++;
                }
            }
        }


        // Only run if at least one value is greater than 0
        if (val > 0) {
            for (let reelNum = 0; reelNum < REEL_NUMBERS; reelNum++) {
                for (let posNum = 0; posNum < SYMBOLS_PER_REEL_FEATURE; posNum++) {
                    const ran = await this.presentOneWinningCell(
                        reelNum,
                        posNum,
                        timeScale,
                        flyTasks
                    );

                    if (ran) {
                        await wait(CELL_STAGGER_MS);
                    }
                }
            }
        }
        else{
            this.winCollectionMeter.visible = false;
        }

        if (flyTasks.length > 0) {
            await Promise.all(flyTasks);
        }
    }

    private openWinCollectionMeter(): void {
        this.spinLeftMeter.visible = false;
        this.winCollectionMeter.visible = true;
        this.winCollectionMeter.tint = "0xffffff";
        this.winCollectionMeter.state.setAnimation(0, "digital_pannel_appear", false);
    }

    private async presentOneWinningCell(
        reelNum: number,
        posNum: number,
        timeScale: number,
        flyTasks: Promise<void>[]
    ): Promise<boolean> {

        const reelSymbol = this.reelOverlaySymbols[reelNum][posNum];
        const symbolId = reelSymbol.symbolId;

        if (symbolId == null || symbolId === BLANK_SYM_ID || !reelSymbol.symbol) {
            return false;
        }

        const didMultiplier = await this.runMultiplierHitIfNeeded(reelSymbol, timeScale);

        reelSymbol.symbol.state.timeScale = timeScale;
        this.applyCollectTint(symbolId, reelSymbol, timeScale);

        const amount = this.collectAmountForCell(reelNum, posNum, didMultiplier);
        reelSymbol.coinVal = amount;
        if (reelSymbol.coinValText) {
            reelSymbol.coinValText.text = `${amount}x`;
            reelSymbol.coinValText.alpha = 1;
        }

        if (this.totalWinVal < 10000 && reelSymbol.coinVal != null) {
            flyTasks.push(this.collectCoinToPanel(reelNum, posNum, amount));
        }

        return true;
    }

    private async runMultiplierHitIfNeeded(reelSymbol: reelOverlayObject, timeScale: number): Promise<boolean> {

        const multVal = reelSymbol.multVal;
        if (!multVal || multVal <= 1 || !reelSymbol.coinValFinalText || !reelSymbol.multValText || !reelSymbol.symbol) {
            return false;
        }

        let base = reelSymbol.coinVal as number;
        const target = base * multVal;

        await new Promise<void>((done) => {
            reelSymbol.symbol!.state.timeScale = timeScale;
            reelSymbol.symbol!.state.setAnimation(0, "multiplier_hit", false).listener = {
                event: (_entry: any, event: any) => {
                    if (event.data.name !== "hit") return;
                    this.animateCoinValueUpdate(reelSymbol.coinValFinalText!, base, target);
                    reelSymbol.coinVal = target;
                    sound.play("multiplier_collect", { volume: 1 });
                    reelSymbol.symbol!.removeSlotObject("multiplier");
                },
                complete: () => {
                    reelSymbol.symbol!.state.timeScale = 1;
                    reelSymbol.symbol!.state.clearListeners();
                    done();
                }
            };
        });

        return true;
    }

    private applyCollectTint(symbolId: number, reelSymbol: reelOverlayObject, timeScale: number): void {

        const sym = reelSymbol.symbol!;

        const finishCollect = (): void => {
            sym.state.timeScale = 1;
            reelSymbol.view.tint = "0x555555";
            sym.state.clearTrack(0);
            sym.state.clearListeners();
        };

        const isCoinVariant =
            (symbolId >= 13 && symbolId <= 21) ||
            (symbolId >= 24 && symbolId <= 26);

        sym.state.clearListeners();
        sym.state.timeScale = timeScale;

        let animName: string;
        if (symbolId === SYMBOL_IDS.Gold_Coin) {
            animName = "appear";
        } else if (isCoinVariant) {
            animName = `${getFeatureSymbolAnimPrefix(symbolId)}loop_blank`;
        } else {
            animName = "appear";
        }

        sym.state.setAnimation(0, animName, false).listener = {
            complete: finishCollect
        };
    }

    private collectAmountForCell(reelNum: number, posNum: number, afterMultiplierHit: boolean): number {

        if (afterMultiplierHit) {
            const v = this.reelOverlaySymbols[reelNum][posNum].coinVal;
            return typeof v === "number" && !Number.isNaN(v) ? v : 0;
        }

        const meterStr = DisplayComponent.get(WinMeterComponent).getInstance().getMeter(reelNum, posNum)?.text?.text;
        if (meterStr?.endsWith("x")) {
            const n = Number(meterStr.slice(0, -1));
            if (!Number.isNaN(n)) return n;
        }

        const serverVal = spinData.respins[RespinIndex.value]?.coinValues?.[reelNum]?.[posNum];
        if (typeof serverVal === "number" && !Number.isNaN(serverVal)) {
            return serverVal;
        }

        const fallback = this.reelOverlaySymbols[reelNum][posNum].coinVal;
        return typeof fallback === "number" && !Number.isNaN(fallback) ? fallback : 0;
    }

    public async lineToggle(currentRespinIndex: number): Promise<void> {
        console.log("inside lineToggle", currentRespinIndex);
    }
}